import type { Certificate } from '@/domain/certificate'
import type { CertificateRepository } from '@/repositories/CertificateRepository'

const STORAGE_KEY = 'cybertales.certificates'

function readAll(): Certificate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Certificate[]) : []
  } catch {
    return []
  }
}

function writeAll(items: Certificate[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
  } catch {
    // best-effort — certificates simply won't persist across reloads.
  }
}

// Zero-cost implementation of CertificateRepository (Document 11 §4).
// CosmosCertificateRepository (P5) implements the same interface against
// the production Certificate Service (server PDF, QR verification, revocation).
export class LocalCertificateRepository implements CertificateRepository {
  async listForUser(userId: string): Promise<Certificate[]> {
    return readAll().filter((c) => c.userId === userId)
  }

  async getById(id: string): Promise<Certificate | null> {
    return readAll().find((c) => c.id === id) ?? null
  }

  async save(certificate: Certificate): Promise<void> {
    const all = readAll()
    const idx = all.findIndex((c) => c.id === certificate.id)
    if (idx >= 0) {
      all[idx] = certificate
    } else {
      all.push(certificate)
    }
    writeAll(all)
  }
}
